/**
 * Preamble · Code · Blocks (DESIGN-14): the tab bar and the remembered choice, shared by the
 * desktop preview and the phone's LaTeX view. Blocks the first time, then the last one used.
 */
import i18n from "i18next";
import { useCallback, useState } from "react";
import { testIds } from "@/testIds";
import styles from "./LatexViewTabs.module.scss";

export type LatexTab = "preamble" | "code" | "blocks";
export const LATEX_TABS: LatexTab[] = ["preamble", "code", "blocks"];
const LATEX_TAB_KEY = "lilia-latex-view-tab";

function initialLatexTab(): LatexTab {
  try {
    const v = localStorage.getItem(LATEX_TAB_KEY);
    return LATEX_TABS.includes(v as LatexTab) ? (v as LatexTab) : "blocks";
  } catch {
    return "blocks";
  }
}

export function useLatexTab(): [LatexTab, (t: LatexTab) => void] {
  const [tab, setTabState] = useState<LatexTab>(initialLatexTab);
  const setTab = useCallback((t: LatexTab) => {
    setTabState(t);
    try { localStorage.setItem(LATEX_TAB_KEY, t); } catch { /* ignore */ }
  }, []);
  return [tab, setTab];
}

export function LatexTabBar({ tab, onChange, className }: { tab: LatexTab; onChange: (t: LatexTab) => void; className?: string }) {
  return (
    <div className={className ?? styles.tabsRow}>
      <div data-testid={testIds.latexView.tabs} className={styles.segmented} role="tablist" aria-label={i18n.t("editor:latexView.tabs.label")}>
        {LATEX_TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            data-testid={testIds.latexView.tab}
            data-tab={t}
            aria-selected={tab === t}
            onClick={() => onChange(t)}
          >
            {i18n.t(`editor:latexView.tabs.${t}`)}
          </button>
        ))}
      </div>
    </div>
  );
}
